import {
  eyeIcon,
  cloudSaveIcon,
  cloudLibraryIcon,
} from "@excalidraw/excalidraw/components/icons";
import { MainMenu } from "@excalidraw/excalidraw/index";
import React from "react";

import { useAuth } from "@clerk/clerk-react";
import { isDevEnv } from "@excalidraw/common";

import type { Theme } from "@excalidraw/element/types";

import { fnUseSupabaseToken } from "../auth/auth";
import { appJotaiStore, useAtomValue } from "../app-jotai";
import {
  bFileBrowserOpenAtom,
  excalidrawAPIAtom,
  fnTriggerCloudSave,
} from "../cloud/cloudState";
import { LanguageList } from "../app-language/LanguageList";

import { saveDebugState } from "./DebugCanvas";

const CloudMenuItems = () => {
  const { isSignedIn } = useAuth();
  const fnGetToken = fnUseSupabaseToken();
  const oExcalidrawAPI = useAtomValue(excalidrawAPIAtom);

  if (!isSignedIn || !oExcalidrawAPI) {
    return null;
  }

  return (
    <>
      <MainMenu.Item
        icon={cloudSaveIcon}
        onSelect={() => fnTriggerCloudSave(oExcalidrawAPI, fnGetToken)}
      >
        Cloud Save
      </MainMenu.Item>
      <MainMenu.Item
        icon={cloudLibraryIcon}
        onSelect={() => appJotaiStore.set(bFileBrowserOpenAtom, true)}
      >
        Cloud Library
      </MainMenu.Item>
    </>
  );
};

export const AppMainMenu: React.FC<{
  onCollabDialogOpen: () => any;
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  theme: Theme | "system";
  setTheme: (theme: Theme | "system") => void;
  refresh: () => void;
}> = React.memo((props) => {
  return (
    <MainMenu>
      <MainMenu.DefaultItems.LoadScene />
      <MainMenu.DefaultItems.SaveToActiveFile />
      <MainMenu.DefaultItems.SaveFileToDisk />
      <CloudMenuItems />
      <MainMenu.Separator />
      <MainMenu.DefaultItems.SaveAsImage />
      <MainMenu.DefaultItems.ShareLink />
      {props.isCollabEnabled && (
        <MainMenu.DefaultItems.LiveCollaborationTrigger
          isCollaborating={props.isCollaborating}
          onSelect={() => props.onCollabDialogOpen()}
        />
      )}
      <MainMenu.DefaultItems.CommandPalette className="highlighted" />
      <MainMenu.DefaultItems.SearchMenu />
      <MainMenu.DefaultItems.Help />
      <MainMenu.DefaultItems.ClearCanvas />
      <MainMenu.Separator />
      <MainMenu.DefaultItems.Socials />
      {isDevEnv() && (
        <MainMenu.Item
          icon={eyeIcon}
          onSelect={() => {
            if (window.visualDebug) {
              delete window.visualDebug;
              saveDebugState({ enabled: false });
            } else {
              window.visualDebug = { data: [] };
              saveDebugState({ enabled: true });
            }
            props?.refresh();
          }}
        >
          Visual Debug
        </MainMenu.Item>
      )}
      <MainMenu.Separator />
      <MainMenu.DefaultItems.Preferences />
      <MainMenu.DefaultItems.ToggleTheme
        allowSystemTheme
        theme={props.theme}
        onSelect={props.setTheme}
      />
      <MainMenu.ItemCustom>
        <LanguageList style={{ width: "100%" }} />
      </MainMenu.ItemCustom>
      <MainMenu.DefaultItems.ChangeCanvasBackground />
    </MainMenu>
  );
});
